import { Component, DoCheck, ElementRef, Input, Optional } from '@angular/core';
import { StateService } from '@uirouter/angular';

import { Subscription } from 'rxjs';

import { BrandingHeaderType } from 'rev-portal/branding/BrandingHeaderType';
import { CALENDAR_STATE_NAME } from 'rev-portal/scheduledEvents/webcast/Constants';
import { HeaderSettings, ThemeSettings } from 'rev-portal/branding/BrandingSettings.Contract';
import { MediaSettingsService } from 'rev-portal/admin/mediaSettings/MediaSettings.Service';
import { MediaStateService, MediaViewMode } from 'rev-portal/media/MediaState.Service';
import { ThemeService } from 'rev-portal/branding/Theme.Service';

import { AppBootstrapService } from 'rev-shared/bootstrap/AppBootstrap.Service';
import { IRules } from 'rev-shared/ui/css/CssRules.Contract';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { RecordingPolicy } from 'rev-shared/media/RecordingPolicy';
import { ScheduledEventService } from 'rev-shared/scheduledEvents/ScheduledEvent.Service';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UserContextService, IUserContextUser, IUserContextAccount } from 'rev-shared/security/UserContext.Service';
import { PORTAL_DASHBOARD_STATE, PORTAL_MEDIA_ALL_STATE, PORTAL_MEDIA_BROWSE_STATE, PORTAL_MEDIA_SEARCH_STATE, PORTAL_VIDEO_PLAYBACK_STATE } from 'rev-shared/bootstrap/AppStateNames';
import { isIe } from 'rev-shared/util/UserAgentUtil';

import { MainHeaderPositionService } from './MainHeaderPosition.Service';

import './main-header.less';

@Component({
	selector: 'main-header',
	templateUrl: './MainHeader.Component.html',
	standalone: false
})
export class MainHeaderComponent implements DoCheck {
	@Input() public mainContent: ElementRef;
	@Input() public isGuestMode: boolean = false;

	private queryTxt: string;

	public themeServiceSub: Subscription;
	public themeStyleOverrides: IRules;
	public accountId: string;
	public event: any;
	public isAnalystAdmin: boolean;
	public showManageSubscriptions: boolean;
	public readonly PORTAL_DASHBOARD_STATE = PORTAL_DASHBOARD_STATE;
	public readonly PORTAL_VIDEO_PLAYBACK_STATE = PORTAL_VIDEO_PLAYBACK_STATE;
	public readonly PORTAL_MEDIA_ALL_STATE = PORTAL_MEDIA_ALL_STATE;
	public readonly PORTAL_MEDIA_BROWSE_STATE = PORTAL_MEDIA_BROWSE_STATE;
	public readonly CALENDAR_STATE_NAME = CALENDAR_STATE_NAME;
	public readonly BrandingHeaderType = BrandingHeaderType;

	public landingState: { state: string, params: any };
	public hideManageSubscriptions: boolean;
	public enableCapture: boolean;
	public enableCategories: boolean;
	public enablePublicWebcastAccess: boolean;
	public enableSignIn: boolean;
	public enableSearch: boolean;

	constructor(
		AppBootstrapService: AppBootstrapService,
		private readonly LoginRedirectService: LoginRedirectService,
		@Optional() private readonly MediaStateService: MediaStateService,
		private readonly SecurityContext: SecurityContextService,
		public readonly MainHeaderPosition: MainHeaderPositionService,
		public readonly MediaFeatures: MediaFeaturesService,
		public readonly MediaSettings: MediaSettingsService,
		public readonly ScheduledEventService: ScheduledEventService,
		public readonly ThemeService: ThemeService,
		public readonly UserContext: UserContextService,
		public readonly $state: StateService,
		public readonly elementRef: ElementRef
	) {
		this.accountId = this.UserContext.getAccount().id;
		this.landingState = AppBootstrapService.getAppLandingState();
		this.hideManageSubscriptions = AppBootstrapService.appConfig?.isRevDistribution;
		const isGuest = this.UserContext.isGuest();
		MediaFeatures.getFeatures().then(features => {
			this.showManageSubscriptions = (features.enableContentNotifications || features.enableSpark) && !this.hideManageSubscriptions;
			this.enableCapture = !isIe() &&
					navigator.mediaDevices?.getUserMedia &&
					features.liveRecordingPolicy &&
					!features.onPrem &&
					!features.webcastSources.webrtcSinglePresenterDisabled &&
					features.recordingPolicy !== RecordingPolicy.DISABLE;
			this.enableCategories = features.enableCategories && (!isGuest || !features.disablePublicBrowseCategories);
			this.enablePublicWebcastAccess = features.enablePublicWebcastAccess && (!isGuest || !features.disablePublicEventsPage);
			this.enableSignIn = !isGuest || !features.disablePublicSignInLink;
			this.enableSearch = !isGuest || !features.disablePublicVideoSearch;
		});
	}

	public ngDoCheck(): void {
		const isTransparencyActive =
			this.MainHeaderPosition.isTransparencySupported &&
			this.ThemeService.brandingSettings.headerSettings.useTransparentHeader;


		this.MainHeaderPosition.setTransparency(isTransparencyActive);
		this.isAnalystAdmin = !this.SecurityContext.hasAuthorizationKey((k => (/admin\.(?!reports)/).test(k)));
	}

	public loginRedirect(): void {
		this.LoginRedirectService.redirectToLogin();
	}

	public get currentTheme(): string {
		return this.headerSettings.brandingHeaderType;
	}

	public get guestPortalRestricted(): boolean {
		return this.isRegisteredGuest || !this.SecurityContext.checkAuthorization('guest');
	}

	public get headerSettings(): HeaderSettings {
		return this.ThemeService.brandingSettings.headerSettings;
	}

	public get isRegisteredGuest(): boolean {
		return this.UserContext.isRegisteredGuest();
	}

	public get isUserAuthenticated(): boolean {
		return this.UserContext.isUserAuthenticated();
	}

	public get themeSettings(): ThemeSettings {
		return this.ThemeService.brandingSettings.themeSettings;
	}

	public get user(): IUserContextUser {
		return this.UserContext.getUser();
	}

	public get account(): IUserContextAccount {
		return this.UserContext.getAccount();
	}

	public submitMediaSearch(): void {
		this.MediaStateService.setViewMode(MediaViewMode.TABLE);
		this.$state.go(PORTAL_MEDIA_SEARCH_STATE, { q: this.queryTxt }, { inherit: false, reload: true });
		this.queryTxt = '';
	}

	public skipNavigation(): void {
		const target = Array.from<HTMLElement>(this.mainContent.nativeElement
			.querySelectorAll('a:not([disabled]):not([hidden]), button:not([disabled]):not([hidden])'))
			.find(el => !findNearest(el, isHidden));

		target?.focus();
	}
}

function isHidden(el: HTMLElement): boolean {
	if(!(el instanceof Element)) {
		return false;
	}

	const style = window.getComputedStyle(el);
	return style.visibility === 'hidden' ||
		style.display === 'none';
}

function findNearest(el: HTMLElement, fn: (el: HTMLElement) => boolean): HTMLElement {
	if(fn(el)) {
		return el;
	}

	let parent = el.parentNode as HTMLElement;
	while(parent) {
		if(fn(parent)) {
			return parent;
		}
		parent = parent.parentNode as HTMLElement;
	}
}
